import * as React from "react";
import { DefaultButton, Dialog, DialogType, MessageBar, MessageBarType, PrimaryButton, Stack, Text } from "office-ui-fabric-react";
import { PageURL } from "../../App";
import "./Welcome.scss";
import { IAuthenticatedUser, IUserAccount } from "../LoginComponent/LoginComponent";
import DataProvider from "../../dataprovider/DataProvider";
import WelcomeBackground from "./../../images/WelcomeBackground.png";
import { AccessRequirements } from "./AccessRequirements";
import { FaqSection } from "./FaqSection";
import { ProcessSteps } from "./ProcessSteps";
import { SignInDialog } from "./SignInDialog";
import { RequestStatus } from "../../Models/Constant";

export interface IWelcomePageProps {
  location: Location;
  history: any;
  stepSizeClass: string;
  isLoggedIn: boolean;
  dataProvider: DataProvider;
  currentUser?: IUserAccount;
  setAuthStatus: (loggedIn: boolean, user: IAuthenticatedUser) => Promise<void>;
}

export interface IWelcomePageState {
  isLoggedIn: boolean;
  currentRequestStatus: string;
  canCreateNewRequest: boolean;
  showInReviewMessage: boolean;
  isDeniedDialogOpen: boolean;
  isSignInDialogOpen: boolean;
}

export class WelcomePage extends React.Component<IWelcomePageProps, IWelcomePageState> {
  constructor(props: IWelcomePageProps) {
    super(props);

    this.state = {
      isLoggedIn: props.isLoggedIn,
      currentRequestStatus: "",
      canCreateNewRequest: false,
      showInReviewMessage: false,
      isDeniedDialogOpen: false,
      isSignInDialogOpen: false,
    };
  }

  public componentDidMount() {
    this.setLoggedInInformation();
  }

  public UNSAFE_componentWillReceiveProps(newProps: IWelcomePageProps) {
    if (this.state.isLoggedIn !== newProps.isLoggedIn || this.props.currentUser !== newProps.currentUser) {
      this.setState({ isLoggedIn: newProps.isLoggedIn }, () => {
        this.setLoggedInInformation();
      });
    }
  }

  private async setLoggedInInformation() {
    if (this.state.isLoggedIn && this.props.currentUser) {
      const response: any = await this.props.dataProvider.getUserData(this.props.currentUser.userId);

      if (response) {
        const currentRequestStatus = response["currentRequestStatus"] ? response["currentRequestStatus"] : "";
        const canCreateNewRequest = response["canCreateNewRequest"] ? response["canCreateNewRequest"] : false;

        if (currentRequestStatus === "AccessApproved") {
          this.props.history.push(PageURL.Download);
        } else if (currentRequestStatus === "AccessDenied" || currentRequestStatus === "AccessRejected") {
          this.setState({
            currentRequestStatus: response["currentRequestStatus"],
            canCreateNewRequest: true,
            showInReviewMessage: false,
            isDeniedDialogOpen: true,
          });
        } else if (!canCreateNewRequest && currentRequestStatus === "DocumentSubmitted") {
          this.setState({
            currentRequestStatus: response["currentRequestStatus"],
            canCreateNewRequest: response["canCreateNewRequest"],
            showInReviewMessage: true,
            isDeniedDialogOpen: false,
          });
        } else if (!canCreateNewRequest && currentRequestStatus === "AskForAccess") {
          this.setState({
            currentRequestStatus: response["currentRequestStatus"],
            canCreateNewRequest: response["canCreateNewRequest"],
            showInReviewMessage: false,
            isDeniedDialogOpen: false,
          });
          this.props.history.push(PageURL.Welcome);
        } else {
          this.setState({
            currentRequestStatus: response["currentRequestStatus"],
            canCreateNewRequest: response["canCreateNewRequest"],
            showInReviewMessage: false,
            isDeniedDialogOpen: false,
          });
        }
      }
    } else {
      this.setState({
        currentRequestStatus: "",
        canCreateNewRequest: false,
        showInReviewMessage: false,
        isDeniedDialogOpen: false,
      });
    }
  }

  private renderPrimaryAction(): React.ReactNode {
    if (!this.state.isLoggedIn) {
      return (
        <Stack className="welcomeButtonColumn" tokens={{ childrenGap: 12 }}>
          <PrimaryButton className="welcomePrimaryAction" onClick={() => this.setState({ isSignInDialogOpen: true })}>
            Sign In
          </PrimaryButton>
          <DefaultButton className="welcomeSecondaryAction" onClick={() => this.props.history.push(PageURL.CreateAccount)}>
            Create Account
          </DefaultButton>
        </Stack>
      );
    }

    if (this.state.canCreateNewRequest) {
      return (
        <Stack className="welcomeButtonColumn">
          <PrimaryButton className="welcomePrimaryAction" onClick={() => this.props.history.push(PageURL.AskAccess)}>
            Ask For Access
          </PrimaryButton>
        </Stack>
      );
    }

    if (this.state.currentRequestStatus === RequestStatus.AskForAccess) {
      return (
        <MessageBar className="welcomeStatusMessage" messageBarType={MessageBarType.info}>
          Please sign the agreement you received via DocuSign. Provided you have signed the agreement, Straumann will review your request and notify you by
          email when the access decision is available.
        </MessageBar>
      );
    }

    return (
      <MessageBar className="welcomeStatusMessage" messageBarType={MessageBarType.info}>
        You are signed in. Your current request status is {this.state.currentRequestStatus || "being prepared"}.
      </MessageBar>
    );
  }

  public render(): React.ReactElement<IWelcomePageProps> {
    const showDeniedMessage = this.state.currentRequestStatus === "AccessDenied" || this.state.currentRequestStatus === "AccessRejected";
    const deniedMessage = showDeniedMessage
      ? "Your previous request was denied. You can submit a new request after reviewing the access requirements below."
      : "";

    return (
      <>
        <div className={`welcomePageShell ${this.state.showInReviewMessage ? "welcomePageShellReview" : ""}`}>
          <div className="welcomeBackgroundLayer" aria-hidden="true">
            <img className="welcomeBackgroundImage" src={WelcomeBackground} alt="" />
          </div>

          <div className="welcomeOverlay">
            {this.state.showInReviewMessage ? (
              <Stack className="welcomeReviewCard" tokens={{ childrenGap: 16 }}>
                <Text className="welcomeReviewTitle">Your request is currently under review</Text>
                <Text className="welcomeReviewBody">
                  Your documents have been submitted successfully. Straumann will review the request and notify you by email when the access decision is
                  available.
                </Text>
              </Stack>
            ) : (
              <div className="welcomeLayout animate popIn">
                <Stack className="welcomePrimaryContent" tokens={{ childrenGap: 24 }}>
                  <Stack tokens={{ childrenGap: 14 }}>
                    <Text className="welcomeHeadline">STL LIBRARY</Text>
                    <Text className="welcomeIntroLine">Access is ONLY available to certified software partners</Text>
                    <Text className="welcomeBodyCopy">
                      Sign in with Google, Microsoft, or your Straumann company account. New software partners can create an account, confirm the activation
                      email, complete the legal agreement workflow, and then download the required STL files once approved.
                    </Text>
                  </Stack>

                  {showDeniedMessage && <Text className="welcomeDeniedMessage">{deniedMessage}</Text>}

                  {this.renderPrimaryAction()}
                  <AccessRequirements />
                </Stack>

                <div className="welcomeBottomContent">
                  <div className="welcomeStepsArea">
                    <ProcessSteps dataProvider={this.props.dataProvider} />
                  </div>

                  <div className="welcomeFaqContainer">
                    <FaqSection />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <SignInDialog
          isOpen={this.state.isSignInDialogOpen}
          onDismiss={() => this.setState({ isSignInDialogOpen: false })}
          history={this.props.history}
          dataProvider={this.props.dataProvider}
          setAuthStatus={this.props.setAuthStatus}
        />

        <Dialog
          hidden={!this.state.isDeniedDialogOpen}
          onDismiss={() => this.setState({ isDeniedDialogOpen: false })}
          dialogContentProps={{
            type: DialogType.normal,
            title: "Access denied",
            closeButtonAriaLabel: "Close",
            showCloseButton: true,
          }}
          modalProps={{
            isBlocking: false,
            className: "welcomeThemeDialog",
            styles: {
              main: {
                maxWidth: 520,
                width: "calc(100% - 32px)",
              },
            },
          }}
        >
          <Text className="welcomeDeniedMessage">{deniedMessage}</Text>
        </Dialog>
      </>
    );
  }
}
